import {
	isTypeSlideChapter,
	TypeExternalLink,
	TypeFinalChapterSkeleton,
	TypeInitialChapterSkeleton,
	TypeMasterChangelog,
	TypeMediaSkeleton,
	TypeSlideChapterSkeleton,
	TypeSvgMapSkeleton,
	TypeTrainSkeleton
} from '@/@types/generated/contentful';
import MapTransform from '@app/transforms/MapTransform';
import ILink from '@app/types/data/base/ILink';
import ChapterItemType from '@app/types/presentations/ChapterItemType';
import IChapterItemMaster from '@app/types/presentations/IChapterItem';
import IChapterMaster from '@app/types/presentations/master/IChapterMaster';
import IPresentationMaster from '@app/types/presentations/master/IPresentationMaster';
import ISectionMaster from '@app/types/presentations/master/ISectionMaster';
import ISlideMaster from '@app/types/presentations/master/ISlideMaster';
import { isCloudinaryAsset } from '@libraries/cloudinary/ICloudinaryAsset';
import { Entry } from 'contentful';
import IMasterChangelogItem from '../../types/IMasterChangelogItem';
import IRawPresentationMaster from '../../types/presentations/master/IRawPresentationMaster';
import MultiLocaleService from '../base/MultiLocaleService';

type ChapterEntry =
	| Entry<TypeSlideChapterSkeleton>
	| Entry<TypeFinalChapterSkeleton>
	| Entry<TypeInitialChapterSkeleton, undefined, string>;

export default class AbstractPresentationMasterService extends MultiLocaleService {
	/**
	 * chapter ids in the order they appear in this presentation master
	 */
	public chapterIds: string[] = [];

	/**
	 * slide ids in the order they appear in this presentation master
	 */
	public slideIds: string[] = [];

	/**
	 * id -> Chapter map of all chapters of this presentation master
	 */
	public chaptersById: Record<string, IChapterMaster> = {};

	/**
	 * id -> Section map of all sections of this presentation master
	 */
	public sectionsById: Record<string, ISectionMaster> = {};

	/**
	 * id -> Slide map of all slides of this presentation master
	 */
	public slidesById: Record<string, ISlideMaster> = {};

	private reset(): void {
		this.slideIds = [];
		this.chapterIds = [];
		this.chaptersById = {};
		this.sectionsById = {};
		this.slidesById = {};
	}

	public mapPresentationMasterEntry(
		rawPresentationMaster: IRawPresentationMaster,
		locale?: string
	): IPresentationMaster {
		this.reset();

		const { fields } = rawPresentationMaster.entry;
		this.chapterIds = [];

		if (fields.initialChapter) {
			this.chapterIds.push(
				this.mapAndIndexChapterEntry(fields.initialChapter as Entry<TypeInitialChapterSkeleton, undefined, string>)
			);
		}

		this.chapterIds.push(
			...(fields.chapters as Entry<TypeSlideChapterSkeleton, undefined, string>[]).map(
				this.mapAndIndexChapterEntry.bind(this)
			)
		);

		if (fields.finalChapter) {
			this.chapterIds.push(
				this.mapAndIndexChapterEntry(fields.finalChapter as Entry<TypeFinalChapterSkeleton, undefined, string>)
			);
		}

		const legalLinks = fields.legalLinks as TypeExternalLink<undefined>[] | undefined;
		const changelogEntries = fields.changelog as TypeMasterChangelog<undefined>[] | undefined;

		let changelog: IMasterChangelogItem[] = [];

		if (changelogEntries) {
			changelog = changelogEntries.map(
				(changelogEntry): IMasterChangelogItem => ({
					id: changelogEntry.sys.id,
					date: Date.parse(changelogEntry.fields.date as string),
					description: changelogEntry.fields.description ?? ''
				})
			);
		}

		const master: IPresentationMaster = {
			id: rawPresentationMaster.id,
			locale: locale as string,
			chapterIds: this.chapterIds,
			slideIds: this.slideIds,
			chaptersById: this.chaptersById,
			sectionsById: this.sectionsById,
			slidesById: this.slidesById,
			checksum: '',
			changelog,
			dateCreated: Date.parse(rawPresentationMaster.entry.sys.createdAt),
			disclaimer: fields.disclaimer,
			legalLinks: legalLinks?.map(
				(linkEntry): ILink => ({
					label: linkEntry.fields.label,
					url: linkEntry.fields.url,
					target: '_blank'
				})
			)
		};

		return {
			...master,
			checksum: rawPresentationMaster.checksum
		};
	}

	/**
	 * Indexes a Contentful slide entry
	 * Returns an id to be stored in the chapter items / sections referencing this slide
	 * @param entry
	 */
	private mapAndIndexSlideEntry(entry: any): string | null {
		// entry is unpublished, skip:
		if (!entry.fields) {
			this.logUnpublishedEntry(entry.sys.id);
			return null;
		}

		const slide: ISlideMaster = {
			id: entry.sys.id,
			dateUpdated: entry.sys.updatedAt,
			type: entry.sys.contentType.sys.id,
			fields: entry.fields
		};

		this.slidesById[slide.id] = slide;
		this.slideIds.push(slide.id);

		return slide.id;
	}

	/**
	 * Indexes a Contentful section entry and trigger indexing of all slides included in the chapter.
	 * Returns an id to be stored in the chapter items referencing this section
	 * @param entry
	 */
	private mapAndIndexSectionEntry(entry: any): string | null {
		// entry is unpublished, skip:
		if (!entry.fields) {
			this.logUnpublishedEntry(entry.sys.id);
			return null;
		}

		const section: ISectionMaster = {
			id: entry.sys.id,
			title: entry.fields.title,
			thumbnail: entry.fields.thumbnail.fields.asset,
			svgId: entry.fields.svgId,
			slides: entry.fields.items
				.map((slideEntry: any): string | null => this.mapAndIndexSlideEntry(slideEntry))
				.filter((slideId: string | null): boolean => slideId !== null) // filter unpublished entries
		};

		this.sectionsById[section.id] = section;

		return section.id;
	}

	/**
	 * Indexes a Contentful chapter entry and trigger indexing of all sections/slides included in the chapter
	 * Returns an id to be stored in the chapterIds array referencing this chapter
	 * @param entry
	 */
	private mapAndIndexChapterEntry(entry: ChapterEntry): string {
		const chapter: IChapterMaster = {
			id: entry.sys.id as string,
			type: entry.sys.contentType.sys.id,
			title: '',
			items: []
		};

		if (isTypeSlideChapter(entry)) {
			const image = entry.fields.image as Entry<TypeMediaSkeleton>;
			const gettingThereImage = entry.fields.gettingThereImage as Entry<TypeMediaSkeleton>;
			const gettingThereTrains = entry.fields.gettingThereTrains as Entry<TypeTrainSkeleton>[];

			if (isCloudinaryAsset(image?.fields.asset)) {
				chapter.image = image?.fields.asset;
			}

			if (gettingThereTrains && gettingThereTrains.length) {
				chapter.gettingThereTrainIds = gettingThereTrains.map((trainEntry): string => trainEntry.sys.id);
			}

			if (isCloudinaryAsset(gettingThereImage?.fields.asset)) {
				chapter.gettingThereImage = gettingThereImage?.fields.asset;
			}

			chapter.title = entry.fields.title as string;
			chapter.svgId = entry.fields.svgId as string;

			if (entry.fields.gettingThereMap) {
				chapter.gettingThereMap = MapTransform.getSvg(entry.fields.gettingThereMap as Entry<TypeSvgMapSkeleton>);
			}
		}

		if (entry.fields.items) {
			chapter.items = (entry.fields.items as Entry[])
				.map((itemEntry): IChapterItemMaster | null => {
					// entry is unpublished, skip:
					if (!itemEntry.sys.contentType) {
						this.logUnpublishedEntry(itemEntry.sys.id);
						return null;
					}

					let type: ChapterItemType = ChapterItemType.Slide;
					let id: string | null;

					if (itemEntry.sys.contentType.sys.id === ChapterItemType.Section) {
						// item is a section
						type = ChapterItemType.Section;
						id = this.mapAndIndexSectionEntry(itemEntry);
					} else {
						// item is a slide
						id = this.mapAndIndexSlideEntry(itemEntry);
					}

					if (!id) return null;

					return {
						type,
						id
					};
				})
				.filter((item: IChapterItemMaster | null): item is IChapterItemMaster => item !== null); // filter unpublished entries
		}

		this.chaptersById[chapter.id] = chapter;

		return chapter.id;
	}

	private logUnpublishedEntry(entryId: string): void {
		console.debug(
			`Contentful entry https://app.contentful.com/spaces/${process.env.CONTENTFUL_SPACE_ID}/entries/${entryId} could bot be loaded as it is unpublished.`
		); // eslint-disable-line no-console
	}
}
